import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bg, line, tx, font, accent, space, radius, attrColour,
} from '../theme';
import Button from '../components/Button';
import { useProfile } from './ProfileContext';
import { useAuth } from '../lib/auth';
import { listSaves, SAVE_LIMIT } from './accountApi';
import { backgroundById, overallRating, finalAttrs } from './dofData';
import { avatarSrc, AVATARS } from './avatars';
import { fetchClubs } from '../game/gameApi';
import ClubCrest from '../game/ClubCrest';
import BrandBackdrop from '../components/BrandBackdrop';
import './Hub.css';
import { ageFromDob } from '../game/gameUtils';
import { clubVars } from '../marketing/clubIdentity';

const TIER_NAMES = { 1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two' };

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tx.muted, fontFamily: font.display, fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86400000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const eyebrow = {
  fontFamily: font.display, fontSize: 10.5, fontWeight: 700,
  letterSpacing: '0.18em', textTransform: 'uppercase',
};

function Avatar({ id, initials, size = 56 }) {
  const [err, setErr] = useState(false);
  const src = avatarSrc(id);
  const mono = (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: bg.raised, border: `1px solid ${line.strong}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: font.display, fontWeight: 700, fontSize: size * 0.36, color: accent.lime,
    }}>{initials}</span>
  );
  if (!src || err) return mono;
  return (
    <img src={src} alt="" onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${line.strong}` }} />
  );
}

// A game-UI block: rounded, gradient, glows and lifts on hover.
function Block({ children, style, glow, onHoverGlow, span, disabled, onMouseMove, onEnter, onLeave }) {
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <div
      onMouseEnter={() => { setHover(true); if (onEnter) onEnter(); }}
      onMouseLeave={() => { setHover(false); if (onLeave) onLeave(); }}
      onMouseMove={onMouseMove}
      style={{
        position: 'relative', overflow: 'hidden',
        borderRadius: radius.lg, padding: space.s5,
        transition: 'transform .16s ease, box-shadow .2s ease',
        transform: active ? 'translateY(-3px)' : 'none',
        boxShadow: active && onHoverGlow ? onHoverGlow : (glow || 'none'),
        gridColumn: span ? '1 / -1' : 'auto',
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function Hub() {
  const { profile, status: profileStatus } = useProfile();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [saves, setSaves] = useState([]);
  const [clubsById, setClubsById] = useState({});
  const [status, setStatus] = useState('loading');
  const [tip, setTip] = useState(null);

  useEffect(() => {
    if (profileStatus !== 'ready' && profileStatus !== 'none') return undefined;
    let alive = true;
    (async () => {
      const [s, c] = await Promise.all([listSaves(), fetchClubs()]);
      if (!alive) return;
      if (s.error) { setStatus('error'); return; }
      setSaves(s.data || []);
      const map = {};
      (c.data || []).forEach((club) => { map[club.id] = club; });
      setClubsById(map);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, [profileStatus]);

  if (profileStatus === 'loading') return <Screen>Loading</Screen>;
  if (profileStatus === 'error') return <Screen>Couldn&rsquo;t load your profile</Screen>;

  const hasProfile = profileStatus === 'ready' && profile;
  const locked = !hasProfile; // can't start a save before creating a profile
  const savesFull = hasProfile && saves.length >= SAVE_LIMIT;
  const d = hasProfile ? (profile.data || {}) : null;
  const firstName = (hasProfile ? (d && d.firstName) : (user?.user_metadata?.firstName)) || '';
  const attrs = hasProfile ? (d.attrs || finalAttrs(d.background, d.bonus)) : null;
  const overall = hasProfile ? overallRating(attrs) : null;
  const age = hasProfile ? ageFromDob(d.dob) : null;

  const plBadges = Object.values(clubsById).filter((c) => c.tier === 1);
  const profileHeadshots = ['m1', 'm2', 'm3', 'f1', 'm4', 'm5', 'f2', 'm6', 'm7']
    .map((id) => AVATARS.find((a) => a.id === id)).filter(Boolean);
  const currentSave = saves[0] || null;
  const currentClub = currentSave ? clubsById[currentSave.club_id] : null;
  const cv = currentClub ? clubVars(currentClub.primary_color) : null;

  return (
    <>
      <BrandBackdrop />
      <div className="gw-hub" style={{ position: 'relative', zIndex: 1 }}>
      <div style={{ marginBottom: space.s6 }}>
        <h1 style={{
          fontFamily: font.display, fontWeight: 700, margin: 0, lineHeight: 1.05,
          fontSize: 'clamp(32px, 4.6vw, 46px)', letterSpacing: '-0.01em',
        }}>
          {firstName ? `Good to see you, ${firstName}.` : 'Good to see you.'}
        </h1>
        <p style={{ color: tx.secondary, marginTop: space.s3, fontSize: 15 }}>
          Where your football story takes shape, from the ground up.
        </p>
      </div>

      {status === 'loading' && <Screen>Loading</Screen>}
      {status === 'error' && <Screen>Couldn&rsquo;t load your hub</Screen>}

      {status === 'ready' && (
        <>
          <div className="gw-hub-grid">
            {/* ── Current save ── */}
            {currentSave && currentClub ? (
              <Block
                style={{
                  background: `linear-gradient(140deg, ${cv.tint}, ${bg.surface} 72%)`,
                  border: `1px solid ${cv.border}`,
                }}
                glow={`0 0 36px -14px ${cv.glow}`}
                onHoverGlow={`0 14px 40px -12px ${cv.glow}`}
              >
                <div style={{ ...eyebrow, color: accent.lime, marginBottom: space.s4 }}>Current save</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: space.s4, marginBottom: space.s5 }}>
                  <ClubCrest club={currentClub} size={68} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 22, color: tx.primary, lineHeight: 1.1 }}>
                      {currentClub.name}
                    </div>
                    <div style={{ fontFamily: font.data, fontSize: 12.5, color: tx.muted, marginTop: 4 }}>
                      {TIER_NAMES[currentClub.tier] || `Tier ${currentClub.tier}`} &middot; last played {timeAgo(currentSave.last_played_at)}
                    </div>
                  </div>
                </div>
                <Button onClick={() => navigate(`/game/${currentSave.id}`)}>Continue &rsaquo;</Button>
              </Block>
            ) : (
              <Block style={{ background: bg.surface, border: `1px dashed ${line.strong}` }}>
                <div style={{ ...eyebrow, color: tx.faint, marginBottom: space.s4 }}>Current save</div>
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  justifyContent: 'center', minHeight: 150,
                }}>
                  <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 20, color: tx.primary, marginBottom: space.s2 }}>
                    No save on the go.
                  </div>
                  <p style={{ color: tx.muted, fontSize: 14, margin: `0 0 ${space.s4}px`, maxWidth: '34ch' }}>
                    Your dugout&rsquo;s empty. Take a club below and start writing your story.
                  </p>
                </div>
              </Block>
            )}

            {/* ── Game profile ── */}
            {!hasProfile ? (
              <div className="gw-step-wrap" style={{ position: 'relative' }}>
                {/* animated hand-drawn Step 1 cue pointing at this block */}
                <div className="gw-step-cue" aria-hidden="true" style={{
                  position: 'absolute', top: -58, right: -92, zIndex: 3,
                  pointerEvents: 'none', textAlign: 'right',
                }}>
                  <div style={{
                    fontFamily: font.display, fontWeight: 700, textTransform: 'uppercase',
                    fontSize: 12, letterSpacing: '0.22em', color: accent.violet,
                  }}>
                    Step 1
                  </div>
                  <div style={{
                    fontFamily: font.display, fontWeight: 600, fontSize: 18,
                    lineHeight: 1.05, color: tx.primary, marginTop: 4, whiteSpace: 'nowrap',
                  }}>
                    Create your profile
                  </div>
                  <svg className="gw-step-arrow" width="74" height="58" viewBox="0 0 74 58" fill="none"
                    style={{ display: 'block', margin: '4px 40px 0 auto' }}>
                    <path className="gw-step-line" pathLength="100" d="M64 5 C 60 27, 48 44, 22 50"
                      stroke={accent.violet} strokeWidth="2.6" strokeLinecap="round" fill="none" />
                    <path className="gw-step-head" d="M22 50 L 37 50 L 30 37 Z" fill={accent.violet} />
                  </svg>
                </div>
              <Block
                style={{
                  background: `linear-gradient(140deg, rgba(200,241,53,0.16), ${bg.surface} 66%)`,
                  border: `1px solid ${accent.lime}`,
                }}
                glow="0 0 40px -14px rgba(200,241,53,0.45)"
                onHoverGlow="0 14px 46px -10px rgba(200,241,53,0.55)"
              >
                {/* faded, angled field of DoF headshots on the right */}
                <div aria-hidden="true" style={{
                  position: 'absolute', right: -24, top: '50%', width: '52%', height: 300,
                  transform: 'translateY(-50%) rotate(-14deg)', transformOrigin: 'center',
                  display: 'flex', flexWrap: 'wrap', gap: 14, alignContent: 'center', justifyContent: 'center',
                  opacity: 0.32, pointerEvents: 'none',
                  WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 58%)',
                  maskImage: 'linear-gradient(90deg, transparent 0%, #000 58%)',
                }}>
                  {profileHeadshots.map((a) => (
                    <img key={a.id} src={a.src} alt="" style={{
                      width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                    }} />
                  ))}
                </div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ ...eyebrow, color: accent.lime, marginBottom: space.s4 }}>Get started</div>
                  <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 24, color: tx.primary, lineHeight: 1.1, marginBottom: space.s2 }}>
                    Create your game profile
                  </div>
                  <p style={{ color: tx.secondary, fontSize: 14, margin: `0 0 ${space.s5}px`, maxWidth: '32ch' }}>
                    Choose your background, your strengths and your look. This is the Director you carry into every save.
                  </p>
                  <Button onClick={() => navigate('/hub/profile')}>Create profile</Button>
                </div>
              </Block>
              </div>
            ) : (
              <Block style={{ background: bg.surface, border: `1px solid ${line.DEFAULT}` }}
                onHoverGlow="0 14px 40px -16px rgba(0,0,0,0.6)">
                <div style={{ ...eyebrow, color: tx.muted, marginBottom: space.s4 }}>Game profile</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: space.s4, marginBottom: space.s5 }}>
                  <Avatar id={d.avatar} initials={`${(d.firstName || '?')[0]}${(d.lastName || '')[0]}`} size={60} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 20, color: tx.primary, lineHeight: 1.1 }}>
                      {d.firstName} {d.lastName}
                    </div>
                    <div style={{ fontFamily: font.data, fontSize: 12.5, color: tx.muted, marginTop: 4 }}>
                      {backgroundById(d.background).id}{d.nationality ? ` · ${d.nationality}` : ''}{age != null ? ` · ${age}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: font.data, fontSize: 26, fontWeight: 700, color: attrColour(overall), lineHeight: 1 }}>{overall}</div>
                    <div style={{ fontFamily: font.display, fontSize: 9, letterSpacing: '0.12em', color: tx.faint }}>OVERALL</div>
                  </div>
                </div>
                <Button variant="ghost" onClick={() => navigate('/hub/profile')}>Manage profile</Button>
              </Block>
            )}

            {/* ── Start a new save (full width) ── */}
            <Block
              span
              disabled={locked}
              onMouseMove={locked ? (e) => setTip({ x: e.clientX, y: e.clientY }) : undefined}
              onLeave={() => setTip(null)}
              style={{
                background: `radial-gradient(900px 300px at 85% -40%, rgba(200,241,53,0.12), transparent 60%), linear-gradient(120deg, ${bg.raised}, ${bg.surface})`,
                border: `1px solid ${line.strong}`,
                display: 'flex', alignItems: 'center', gap: space.s5, flexWrap: 'wrap',
                minHeight: 210,
                opacity: locked ? 0.55 : 1,
                cursor: locked ? 'not-allowed' : 'default',
              }}
              onHoverGlow={locked ? undefined : '0 14px 46px -14px rgba(200,241,53,0.35)'}
            >
              {/* faded, angled field of Premier League badges on the right */}
              {plBadges.length > 0 && (
                <div aria-hidden="true" style={{
                  position: 'absolute', right: -30, top: '50%', width: '48%', height: 320,
                  transform: 'translateY(-50%) rotate(-14deg)', transformOrigin: 'center',
                  display: 'flex', flexWrap: 'wrap', gap: 20, alignContent: 'center', justifyContent: 'center',
                  opacity: 0.3, pointerEvents: 'none',
                  WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)',
                  maskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)',
                }}>
                  {plBadges.map((c) => (
                    <ClubCrest key={c.id} club={c} size={56} />
                  ))}
                </div>
              )}

              <div style={{ position: 'relative', zIndex: 1, minWidth: 0, flex: 1 }}>
                <div style={{ ...eyebrow, color: accent.lime, marginBottom: space.s2 }}>New save</div>
                <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 26, color: tx.primary, lineHeight: 1.1 }}>
                  Start a new save
                </div>
                <p style={{ color: tx.secondary, fontSize: 14.5, margin: `${space.s2}px 0 0`, maxWidth: '52ch' }}>
                  Take charge of any club across the four divisions and build something of your own.
                </p>
                {savesFull && (
                  <div style={{ marginTop: space.s3, fontFamily: font.data, fontSize: 12.5, color: accent.amber }}>
                    All {SAVE_LIMIT} save slots are in use. Free one to start another.
                  </div>
                )}
              </div>
              <Button
                onClick={() => { if (locked) return; navigate(savesFull ? '/hub/saves' : '/hub/new'); }}
                disabled={locked}
                style={{ position: 'relative', zIndex: 1, fontSize: 14, padding: '18px 26px' }}
              >
                {savesFull ? 'Manage saves' : 'Take a club'} &rsaquo;
              </Button>
            </Block>
          </div>

        </>
      )}

      {locked && tip && (
        <div style={{
          position: 'fixed', left: tip.x + 14, top: tip.y + 16, zIndex: 200,
          pointerEvents: 'none', background: bg.overlay, border: `1px solid ${line.strong}`,
          borderRadius: radius.sm, padding: '6px 10px', fontFamily: font.display,
          fontSize: 12, fontWeight: 600, color: tx.primary,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', whiteSpace: 'nowrap',
        }}>
          Create a profile first
        </div>
      )}
    </div>
    </>
  );
}
